import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { FilterControlType } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/filter.control";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, filter } from "rxjs";
import {
  AccountFilter,
  CategoryFilter,
  CountryFilter,
  InstitutionFilter,
  MainFilter,
  AllowedMainFilterType,
  ScenarioFilterControlType,
  PastMainFilterType,
} from "@bitwarden/web-vault/app/models/types/filter.types";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { ScenariosFilter } from "../views-customize/scenarios.filter";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { DropDownOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";
import { ViewsTodayFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/views-today.filter";
import { ViewsPastFilter } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/views-past.filter";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

@Component({
  selector: "app-views-dropdown",
  templateUrl: "./views-dropdown.component.html",
})
export class ViewsDropdownComponent implements OnInit {
  @Output() selectedOptionsChange = new EventEmitter<any>();
  @Input() closeDialog?: () => void;

  /** Reset/Confirm. Opened in a dialog-wrapper, they go in its footer slot, pinned to the panel's
   * bottom like the other right-panel dialogs' actions; anywhere else they render inline. */
  @ViewChild("footer", { static: true }) private footer: TemplateRef<unknown>;
  private dialogData = inject<{ footerTemplate?: TemplateRef<unknown> } | null>(MAT_DIALOG_DATA, {
    optional: true,
  });
  protected inDialogFooter = false;

  ngOnInit(): void {
    if (!this.dialogData) {
      return;
    }
    this.inDialogFooter = true;
    // Deferred: the wrapper has already evaluated its footer slot by the time this (dynamically
    // created) child initialises, so a synchronous write would trip NG0100 in dev mode. The
    // microtask runs in the Angular zone, so the next tick renders the footer.
    queueMicrotask(() => (this.dialogData.footerTemplate = this.footer));
  }
  protected readonly View = View;

  protected calculationPastStoreService: CalculationPastStoreService = inject(
    CalculationPastStoreService,
  );
  protected calculationTodayStoreService: CalculationTodayStoreService = inject(
    CalculationTodayStoreService,
  );
  protected scenarioStoreService: ScenarioStoreService = inject(ScenarioStoreService);
  protected i18nService: I18nService = inject(I18nService);
  protected confirmationDialogService = inject(ConfirmationDialogService);
  protected viewsService = inject(ViewsService);
  protected viewsPastFilter = inject(ViewsPastFilter);
  protected viewsTodayFilter = inject(ViewsTodayFilter);
  protected scenariosFilter = inject(ScenariosFilter);
  protected userStoreService = inject(UserStoreService);
  private transactionStoreService = inject(TransactionStoreService);

  protected cancelButtonOptions = new ActionButton({
    class: "neutral",
    text: this.i18nService.t("reset"),
    onClick: this.onCancel.bind(this),
    testId: "viewsDropdownCancelButton",
  });

  protected confirmButtonOptions = new ActionButton({
    class: "teal",
    text: this.i18nService.t("confirm"),
    onClick: this.onConfirm.bind(this),
    testId: "viewsDropdownConfirmButton",
  });

  countries: Array<DropDownOption> = [];
  selectedCountries: string[] = [];

  institutions: Array<DropDownOption> = [];
  selectedInstitutions: string[] = [];
  disabledInstitutions: string[] = [];

  accounts: Array<DropDownOption> = [];
  selectedAccounts: string[] = [];
  disabledAccounts: string[] = [];

  categories: Array<DropDownOption> = [];
  selectedCategories: string[] = [];
  disabledCategories: string[] = [];

  constructor() {
    if (this.viewsService.view === View.PAST) {
      this.viewsPastFilter.filterObservable$
        .pipe(
          filter((x) => !!x),
          // NOTE: must compare with the === and not stringfy because it is a map type
          distinctUntilChanged((x, y) => x === y),
          takeUntilDestroyed(),
        )
        .subscribe((filter) => {
          if (filter) {
            this.setLists(filter);
          }
        });
      this.viewsPastFilter.syncFromStore();
    } else if (this.viewsService.view === View.TODAY) {
      this.viewsTodayFilter.filterObservable$
        .pipe(
          filter((x) => !!x),
          distinctUntilChanged((x, y) => x === y),
          takeUntilDestroyed(),
        )
        .subscribe((filter) => {
          if (filter) {
            this.setLists(filter);
          }
        });

      this.viewsTodayFilter.syncFromStore();
    } else if (this.viewsService.view === View.FUTURE) {
      this.scenariosFilter.filterObservable$
        .pipe(
          filter((x) => !!x),
          distinctUntilChanged((x, y) => x === y),
          takeUntilDestroyed(),
        )
        .subscribe((filter) => {
          if (filter) {
            this.setLists(filter);
          }
        });

      this.scenariosFilter.syncFromStore();
    }
  }

  private setLists(filter: MainFilter<AllowedMainFilterType>) {
    const countriesFilter: CountryFilter[] = [];
    const institutionsFilter: InstitutionFilter[] = [];
    const accountsFilter: AccountFilter[] = [];
    for (const [countryCode, countryFilter] of filter.countryFilter) {
      countriesFilter.push(countryFilter);
      for (const [institutionId, institutionFilter] of countryFilter.childFilter) {
        institutionsFilter.push(institutionFilter);
        for (const [accountId, accountFilter] of institutionFilter.childFilter) {
          accountsFilter.push(accountFilter);
        }
      }
    }

    this.setCountriesList(countriesFilter);
    this.setInstitutionsList(institutionsFilter);
    this.setAccountsList(accountsFilter);

    if (this.hasCategoryFilter(filter)) {
      const categoriesFilter: CategoryFilter[] = [];
      for (const [categoryId, categoryFilter] of filter.categoryFilter) {
        categoriesFilter.push(categoryFilter);
      }
      this.setCategoriesList(categoriesFilter);
    }
  }

  private setCountriesList(countriesFilter: CountryFilter[]) {
    const countries: Array<DropDownOption> = [];
    const selectedCountries: string[] = [];
    countriesFilter.forEach((countryFilter, index) => {
      countries.push({ id: countryFilter.view.code, name: countryFilter.view.name });
      if (countryFilter.isSelected) {
        selectedCountries.push(countryFilter.view.code);
      }
    });
    this.countries = countries;
    this.selectedCountries = selectedCountries;
  }

  private setInstitutionsList(institutionsFilter: InstitutionFilter[]) {
    const institutions: Array<DropDownOption> = [];
    const selectedInstitutions: string[] = [];
    const disabledInstitutions: string[] = [];
    institutionsFilter.forEach((institutionFilter, index) => {
      institutions.push({
        id: institutionFilter.view.id,
        name: institutionFilter.view.name,
      });
      if (institutionFilter.isSelected) {
        selectedInstitutions.push(institutionFilter.view.id);
      }
      if (institutionFilter.isDisabled) {
        disabledInstitutions.push(institutionFilter.view.id);
      }
    });
    this.institutions = institutions;
    this.selectedInstitutions = selectedInstitutions;
    this.disabledInstitutions = disabledInstitutions;
  }

  private setAccountsList(accountsFilter: AccountFilter[]) {
    const accounts: Array<DropDownOption> = [];
    const selectedAccounts: string[] = [];
    const disabledAccounts: string[] = [];
    accountsFilter.forEach((accountFilter, index) => {
      accounts.push({ id: accountFilter.view.id, name: accountFilter.view.name });
      if (accountFilter.isSelected) {
        selectedAccounts.push(accountFilter.view.id);
      }
      if (accountFilter.isDisabled) {
        disabledAccounts.push(accountFilter.view.id);
      }
    });
    this.accounts = accounts;
    this.selectedAccounts = selectedAccounts;
    this.disabledAccounts = disabledAccounts;
  }

  private setCategoriesList(categoriesFilter: CategoryFilter[]) {
    const categories: Array<DropDownOption> = [];
    const selectedCategories: string[] = [];
    const disabledCategories: string[] = [];
    const allTransactions = this.transactionStoreService.enrichTransaction.collection();
    const allCategoryViews = this.userStoreService.categories.categoryViews();

    const filterStateMap = new Map<string, CategoryFilter>();

    categoriesFilter.forEach((f) => {
      if (f?.view?.id) {
        filterStateMap.set(f.view.id, f);
      }
    });

    // Count Map for categories
    const countMap = new Map<string, number>();
    allTransactions.forEach((t) => {
      t.c_categories?.forEach((catId) => {
        if (catId) {
          countMap.set(catId, (countMap.get(catId) || 0) + 1);
        }
      });
    });

    // Process all category views
    allCategoryViews.forEach((catView) => {
      if (catView?.id) {
        const catId = catView.id;
        const count = countMap.get(catId) || 0;
        const filterItem = filterStateMap.get(catId);

        categories.push({
          id: catId,
          name: `${catView.name || "Unknown"} (${count})`,
        });

        if (filterItem?.isSelected) {
          selectedCategories.push(catId);
        }
        if (filterItem?.isDisabled) {
          disabledCategories.push(catId);
        }
      }
    });
    this.categories = categories;
    this.selectedCategories = [...selectedCategories];
    this.disabledCategories = [...disabledCategories];
  }

  // Each handler keeps the selection it was handed: app-dropdown-multiselect emits a new array
  // rather than changing the bound one, and onConfirm reads these, so they mustn't wait for the
  // filter's round trip (setLists) to catch up.
  protected onCountrySelect(countryIds: string[]) {
    this.selectedCountries = countryIds;
    if (this.viewsService.view === View.PAST) {
      this.viewsPastFilter.onCountrySelect(countryIds);
    } else if (this.viewsService.view === View.TODAY) {
      this.viewsTodayFilter.onCountrySelect(countryIds);
    } else if (this.viewsService.view === View.FUTURE) {
      this.scenariosFilter.onCountrySelect(countryIds);
    }
  }

  protected onInstitutionSelect(instoIds: string[]) {
    this.selectedInstitutions = instoIds;
    if (this.viewsService.view === View.PAST) {
      this.viewsPastFilter.onInstitutionSelect(instoIds);
    } else if (this.viewsService.view === View.TODAY) {
      this.viewsTodayFilter.onInstitutionSelect(instoIds);
    } else if (this.viewsService.view === View.FUTURE) {
      this.scenariosFilter.onInstitutionSelect(instoIds);
    }
  }

  protected onAccountSelect(accountIds: string[]) {
    this.selectedAccounts = accountIds;
    if (this.viewsService.view === View.PAST) {
      this.viewsPastFilter.onAccountSelect(accountIds);
    } else if (this.viewsService.view === View.TODAY) {
      this.viewsTodayFilter.onAccountSelect(accountIds);
    } else if (this.viewsService.view === View.FUTURE) {
      this.scenariosFilter.onAccountSelect(accountIds);
    }
  }

  protected onCategorySelect(catIds: string[]) {
    this.selectedCategories = catIds;
    if (this.viewsService.view === View.PAST) {
      this.viewsPastFilter.onCategorySelect(catIds);
    }
  }

  onCancel(): void {
    if (this.viewsService.view === View.PAST) {
      this.viewsPastFilter.resetFilter();
    } else if (this.viewsService.view === View.TODAY) {
      this.viewsTodayFilter.resetFilter();
    } else if (this.viewsService.view === View.FUTURE) {
      this.scenariosFilter.resetFilter();
    }
  }

  onConfirm(): void {
    const institutions = this.selectedInstitutions;
    const accounts = this.selectedAccounts;
    const countries = this.selectedCountries;
    const categories = this.selectedCategories;
    if (!institutions.length) {
      this.confirmationDialogService.notify(
        "error",
        "errorOccurred",
        "selectAtLeastOneInstitution",
      );
      return;
    }

    if (!accounts.length) {
      this.confirmationDialogService.notify("error", "errorOccurred", "selectAtLeastOneAccount");
      return;
    }

    if (!countries.length) {
      this.confirmationDialogService.notify("error", "errorOccurred", "selectAtLeastOneCountry");
      return;
    }

    // Only the PAST view's filter carries categories (TODAY/FUTURE below never spread them into
    // newFilter) — an empty selection here isn't self-healing the way it used to be, since
    // reconcileCategorySelection no longer falls back to the full category list for an
    // intentionally-empty one (see review of #4593/#4175). Blanks every transaction in
    // FilterControl.applyFiltersOnTransaction ([].some(...) is always false), so guard it the
    // same as the other three before it can reach the store.
    if (this.viewsService.currentView() === View.PAST && !categories.length) {
      this.confirmationDialogService.notify("error", "errorOccurred", "selectAtLeastOneCategory");
      return;
    }

    if (this.viewsService.currentView() === View.PAST) {
      const newFilter: FilterControlType = {
        ...this.calculationPastStoreService.filters.selected(),
        countries,
        institutions,
        accounts,
        categories,
      };

      this.calculationPastStoreService.filters.updateSelection(newFilter);
      this.selectedOptionsChange.emit(this.calculationPastStoreService.filters.selected());
    } else if (this.viewsService.currentView() === View.TODAY) {
      const newFilter: FilterControlType = {
        ...this.calculationTodayStoreService.filters.selected(),
        countries,
        institutions,
        accounts,
      };

      this.calculationTodayStoreService.filters.updateSelection(newFilter);
      this.selectedOptionsChange.emit(this.calculationTodayStoreService.filters.selected());
    } else if (this.viewsService.currentView() === View.FUTURE) {
      const newFilter: ScenarioFilterControlType = {
        ...this.scenarioStoreService.filters.selected(),
        institutions,
        accounts,
      };

      this.scenarioStoreService.filters.updateSelection(newFilter);
      this.selectedOptionsChange.emit(this.scenarioStoreService.filters.selected());
    }

    this.closeDialog?.();
  }

  hasCategoryFilter(
    filter: MainFilter<AllowedMainFilterType>,
  ): filter is MainFilter<PastMainFilterType> {
    return "categoryFilter" in filter;
  }
}
